import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import warehousesService from '../../services/warehousesService';
import { useLocation } from '../../contexts/LocationContext';
import {
  AlertBanner,
  DetailsModal,
} from './WarehouseDocumentBase';

// Dzisiejsza data w strefie UZYTKOWNIKA. new Date().toISOString() zwraca UTC,
// wiec wieczorem w Polsce (UTC+2) podstawialby date WCZORAJSZA.
const dzisiajLokalnie = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const InterWarehouseTransfer = () => {
  const { selectedLocation, locationId } = useLocation();
  const currentLocationId = locationId;

  const [warehouses, setWarehouses] = useState([]);
  const [sourceWarehouses, setSourceWarehouses] = useState([]);
  // Cele przesuniecia - cala siec, osobny endpoint (patrz loadData)
  const [targetWarehouses, setTargetWarehouses] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  // Filtry
  const [filters, setFilters] = useState({
    status: '',
    warehouse_id: ''
  });

  // Form tworzenia transferu
  const [transferForm, setTransferForm] = useState({
    magazyn_zrodlowy_id: '',
    magazyn_docelowy_id: '',
    uwagi: '',
    items: [],
    document_date: dzisiajLokalnie()
  });

  // Form dodawania produktu do transferu
  const [productForm, setProductForm] = useState({
    produkt_id: '',
    ilosc: '1',
    cena_jednostkowa: ''
  });

  // Wyszukiwanie produktow - autocomplete
  const [productSearch, setProductSearch] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [onlyAvailable, setOnlyAvailable] = useState(true);

  useEffect(() => {
    loadData();
  }, [filters, currentLocationId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [warehousesRes, transfersRes] = await Promise.all([
        warehousesService.getWarehousesWithStatus(),
        warehousesService.getTransfers(filters)
      ]);

      if (warehousesRes.success) {
        const allWarehouses = warehousesRes.data;
        setWarehouses(allWarehouses);

        let lokalne = allWarehouses;
        if (currentLocationId) {
          lokalne = allWarehouses.filter(
            w => w.location_id === currentLocationId || w.lokalizacja_id === currentLocationId
          );
        }
        setSourceWarehouses(lokalne);

        // Zrodlo domyslnie = magazyn lokalizacji, na ktorej user jest zalogowany.
        // Gdy jest dokladnie jeden, nie ma czego wybierac.
        if (lokalne.length === 1) {
          setTransferForm(prev => (
            prev.magazyn_zrodlowy_id ? prev : { ...prev, magazyn_zrodlowy_id: String(lokalne[0].id) }
          ));
        }
      }

      // Cele przesuniecia to CALA siec - GET /warehouses jest ograniczony do
      // lokalizacji uzytkownika, wiec bez tego kasjer widzialby jako cel
      // wylacznie swoj wlasny magazyn i nie mogl zrobic przesuniecia.
      try {
        const celeRes = await warehousesService.getTransferTargets();
        if (celeRes.success && Array.isArray(celeRes.data) && celeRes.data.length > 0) {
          setTargetWarehouses(celeRes.data);
        } else {
          setTargetWarehouses(warehousesRes.success ? warehousesRes.data : []);
        }
      } catch (e) {
        // Brak uprawnienia magazyn/przesuniecia albo starszy backend -
        // wracamy do listy widocznej dla uzytkownika, zamiast pustego wyboru.
        setTargetWarehouses(warehousesRes.success ? warehousesRes.data : []);
      }
      if (transfersRes.success) {
        setTransfers(transfersRes.data?.transfers || transfersRes.data || []);
      }
    } catch (err) {
      setError('Błąd podczas ładowania danych');
    } finally {
      setLoading(false);
    }
  };

  const searchTimeoutRef = React.useRef(null);

  const loadProducts = async (searchQuery = '') => {
    try {
      let url = searchQuery
        ? `/products/search?limit=20&query=${encodeURIComponent(searchQuery)}`
        : '/products/search?limit=20';

      if (currentLocationId) {
        url += `&location_id=${currentLocationId}`;
      }

      const response = await api.get(url);
      const result = response.data;
      const prods = result.data?.products || result.products || [];
      setProducts(prods);

      let filtered = prods;
      if (onlyAvailable) {
        filtered = filtered.filter(p => (p.stock_quantity || 0) > 0);
      }
      setFilteredProducts(filtered.slice(0, 15));
      setShowProductDropdown(filtered.length > 0);
    } catch (err) {
      console.error('❌ Błąd ładowania produktów:', err);
    }
  };

  // Wyszukiwanie z debounce 300ms — zapytanie do API
  useEffect(() => {
    if (productSearch.trim().length < 2) {
      setFilteredProducts([]);
      setShowProductDropdown(false);
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      loadProducts(productSearch.trim());
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [productSearch, onlyAvailable]);

  const handleSelectProduct = (product) => {
    const existingIndex = transferForm.items.findIndex(item => item.produkt_id === product.id);

    if (existingIndex >= 0) {
      setTransferForm(prev => ({
        ...prev,
        items: prev.items.map((item, idx) =>
          idx === existingIndex
            ? { ...item, ilosc_wyslana: item.ilosc_wyslana + 1 }
            : item
        )
      }));
    } else {
      const purchasePrice = product.current_purchase_price || product.cena_zakupu || product.purchase_price || 0;
      const stockQty = product.stock_quantity || product.stan_magazynowy || 0;
      const newItem = {
        produkt_id: product.id,
        nazwa_produktu: product.name || product.nazwa,
        kod_kreskowy: product.barcode || product.kod_kreskowy,
        ilosc_wyslana: 1,
        cena_jednostkowa: purchasePrice,
        dostepna_ilosc: stockQty
      };

      setTransferForm(prev => ({
        ...prev,
        items: [...prev.items, newItem]
      }));
    }

    setProductSearch('');
    setShowProductDropdown(false);
    setError(null);
  };

  const handleUpdateItemQuantity = (index, newQuantity) => {
    const qty = parseFloat(newQuantity);
    if (isNaN(qty) || qty <= 0) return;

    setTransferForm(prev => ({
      ...prev,
      items: prev.items.map((item, idx) =>
        idx === index ? { ...item, ilosc_wyslana: qty } : item
      )
    }));
  };

  const handleUpdateItemPrice = (index, newPrice) => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) return;

    setTransferForm(prev => ({
      ...prev,
      items: prev.items.map((item, idx) =>
        idx === index ? { ...item, cena_jednostkowa: price } : item
      )
    }));
  };

  useEffect(() => {
    if (showCreateModal) {
      loadProducts();
    }
  }, [showCreateModal]);

  const handleCreateTransfer = async (e) => {
    e.preventDefault();

    if (transferForm.items.length === 0) {
      setError('Dodaj przynajmniej jeden produkt do transferu');
      return;
    }

    setLoading(true);
    try {
      const result = await warehousesService.createTransfer(transferForm);

      if (result.success) {
        setSuccess(result.message);
        setShowCreateModal(false);
        resetTransferForm();
        loadData();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Błąd podczas tworzenia transferu');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduct = () => {
    if (!selectedProduct || !productForm.ilosc) {
      setError('Wybierz produkt i podaj ilość');
      return;
    }

    const newItem = {
      produkt_id: selectedProduct.id,
      nazwa_produktu: selectedProduct.name || selectedProduct.nazwa,
      kod_kreskowy: selectedProduct.barcode || selectedProduct.kod_kreskowy,
      ilosc_wyslana: parseFloat(productForm.ilosc),
      cena_jednostkowa: parseFloat(productForm.cena_jednostkowa) || 0
    };

    const exists = transferForm.items.some(item => item.produkt_id === newItem.produkt_id);
    if (exists) {
      setError('Ten produkt jest już na liście');
      return;
    }

    setTransferForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    setProductForm({
      produkt_id: '',
      ilosc: '1',
      cena_jednostkowa: ''
    });
    setProductSearch('');
    setSelectedProduct(null);
    setError(null);
  };

  const handleRemoveProduct = (index) => {
    setTransferForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleTransferAction = async (transferId, action, items = []) => {
    const actionLabels = {
      'approve': 'zatwierdzić',
      'ship': 'wysłać',
      'receive': 'odebrać',
      'cancel': 'anulować'
    };
    if (!window.confirm(`Czy na pewno chcesz ${actionLabels[action] || action} ten transfer?`)) {
      return;
    }

    setLoading(true);
    try {
      const result = await warehousesService.updateTransferStatus(transferId, action, items);

      if (result.success) {
        setSuccess(result.message);
        loadData();
        if (showDetailsModal) {
          viewTransferDetails(transferId);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(`Błąd podczas wykonywania akcji: ${actionLabels[action] || action}`);
    } finally {
      setLoading(false);
    }
  };

  const viewTransferDetails = async (transferId) => {
    try {
      const result = await warehousesService.getTransferDetails(transferId);
      if (result.success) {
        setSelectedTransfer(result.data);
        setShowDetailsModal(true);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Błąd podczas pobierania szczegółów transferu');
    }
  };

  const resetTransferForm = () => {
    setTransferForm({
      magazyn_zrodlowy_id: sourceWarehouses.length === 1 ? String(sourceWarehouses[0].id) : '',
      magazyn_docelowy_id: '',
      document_date: dzisiajLokalnie(),
      uwagi: '',
      items: []
    });
    setProductForm({
      produkt_id: '',
      ilosc: '1',
      cena_jednostkowa: ''
    });
    setProductSearch('');
    setSelectedProduct(null);
    setShowProductDropdown(false);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'oczekujacy': { bg: 'var(--text-muted)', text: 'Oczekujący' },
      'zatwierdzony': { bg: 'var(--info)', text: 'Zatwierdzony' },
      'w_transporcie': { bg: 'var(--warning)', color: 'var(--text)', text: 'W transporcie' },
      'dostarczony': { bg: 'var(--success)', text: 'Dostarczony' },
      'anulowany': { bg: 'var(--danger)', text: 'Anulowany' },
      'utworzone': { bg: 'var(--text-muted)', text: 'Utworzone' },
      'wyslane': { bg: 'var(--warning)', color: 'var(--text)', text: 'Wysłane' },
      'otrzymane': { bg: 'var(--success)', text: 'Otrzymane' },
      'anulowane': { bg: 'var(--danger)', text: 'Anulowane' }
    };

    const statusInfo = statusMap[status] || { bg: 'var(--border)', color: 'var(--text)', text: status };
    return (
      <span style={{
        fontSize: '10px',
        padding: '2px 6px',
        backgroundColor: statusInfo.bg,
        color: statusInfo.color || 'white',
        borderRadius: '3px'
      }}>
        {statusInfo.text}
      </span>
    );
  };

  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.nazwa : 'Nieznany magazyn';
  };

  const isFromSourceWarehouse = (transfer) => {
    const sourceWarehouse = warehouses.find(w => w.id === transfer.magazyn_zrodlowy_id);
    return sourceWarehouse && sourceWarehouse.location_id === currentLocationId;
  };

  const isFromDestinationWarehouse = (transfer) => {
    const destWarehouse = warehouses.find(w => w.id === transfer.magazyn_docelowy_id);
    return destWarehouse && destWarehouse.location_id === currentLocationId;
  };

  return (
    <div className="inter-warehouse-transfer" style={{ fontSize: '12px' }}>
      <AlertBanner type="error" message={error} onClose={() => setError(null)} />
      <AlertBanner type="success" message={success} onClose={() => setSuccess(null)} />

      <div className="card" style={{ fontSize: '11px' }}>
        <div className="card-header d-flex justify-content-between align-items-center" style={{ padding: '0.4rem 0.75rem' }}>
          <h6 style={{ margin: 0, fontWeight: '600', fontSize: '13px' }}>🔄 Transfery mi{'ę'}dzymagazynowe</h6>
          <button
            style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--success)', color: 'var(--success)' , cursor: 'pointer' }}
            onClick={() => {
              resetTransferForm();
              setShowCreateModal(true);
            }}
          >
            {'➕'} Nowy transfer
          </button>
        </div>

        <div className="card-body" style={{ padding: '0.5rem 0.75rem' }}>
          {/* Filters */}
          <div className="row mb-2">
            <div className="col-md-4">
              <select
                style={{ width: '100%', fontSize: '12px', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
              >
                <option value="">Wszystkie statusy</option>
                <option value="oczekujacy">Oczekuj{'ą'}cy</option>
                <option value="zatwierdzony">Zatwierdzony</option>
                <option value="w_transporcie">W transporcie</option>
                <option value="dostarczony">Dostarczony</option>
                <option value="anulowany">Anulowany</option>
              </select>
            </div>
            <div className="col-md-4">
              <select
                style={{ width: '100%', fontSize: '12px', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
                value={filters.warehouse_id}
                onChange={(e) => setFilters({...filters, warehouse_id: e.target.value})}
              >
                <option value="">Wszystkie magazyny</option>
                {warehouses.map(warehouse => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.nazwa}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <button
                style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--card-bg)', color: 'var(--info)', border: '1px solid var(--info)', cursor: 'pointer' }}
                onClick={loadData}
              >
                🔄 Od{'ś'}wie{'ż'}
              </button>
            </div>
          </div>

          {/* Transfer list */}
          {loading ? (
            <div className="text-center py-2">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">{'Ł'}adowanie...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Numer', 'Magazyn źródłowy', 'Magazyn docelowy', 'Status', 'Data utworzenia', 'Akcje'].map((h, i) => (
                      <th key={i} style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'left', fontWeight: '600', color: 'var(--text-secondary)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transfers.map(transfer => (
                    <tr key={transfer.id} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '0.3rem 0.5rem' }}><code style={{ fontSize: '10px', backgroundColor: 'var(--border)', padding: '1px 4px', borderRadius: '2px' }}>{transfer.numer_transferu}</code></td>
                      <td style={{ padding: '0.3rem 0.5rem' }}>{transfer.magazyn_zrodlowy_nazwa || getWarehouseName(transfer.magazyn_zrodlowy_id)}</td>
                      <td style={{ padding: '0.3rem 0.5rem' }}>{transfer.magazyn_docelowy_nazwa || getWarehouseName(transfer.magazyn_docelowy_id)}</td>
                      <td style={{ padding: '0.3rem 0.5rem' }}>{getStatusBadge(transfer.status)}</td>
                      <td style={{ padding: '0.3rem 0.5rem' }}>{new Date(transfer.data_zlozenia || transfer.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: '0.3rem 0.5rem' }}>
                        <button
                          style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--info)', color: 'var(--info)' , marginRight: '0.25rem', cursor: 'pointer' }}
                          onClick={() => viewTransferDetails(transfer.id)}
                        >
                          👁️
                        </button>
                        {(transfer.status === 'oczekujacy' || transfer.status === 'zatwierdzony') && isFromSourceWarehouse(transfer) && (
                          <button
                            style={{ padding: '4px 10px', fontSize: '11px', backgroundColor: 'var(--warning)', color: 'var(--text)', marginRight: '0.25rem', cursor: 'pointer' }}
                            onClick={() => handleTransferAction(transfer.id, 'ship')}
                            title="Wyślij"
                          >
                            📦 Wy{'ś'}lij
                          </button>
                        )}
                        {transfer.status === 'w_transporcie' && isFromDestinationWarehouse(transfer) && (
                          <button
                            style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--success)', color: 'var(--success)' , cursor: 'pointer' }}
                            onClick={() => handleTransferAction(transfer.id, 'receive')}
                            title="Potwierdź odbiór"
                          >
                            {'✅'} Odbierz
                          </button>
                        )}
                        {(transfer.status === 'oczekujacy' || transfer.status === 'zatwierdzony') && isFromDestinationWarehouse(transfer) && (
                          <span style={{ fontSize: '9px', color: 'var(--text-muted)' }}>{'⏳'} Oczekuje na wysy{'ł'}k{'ę'}</span>
                        )}
                        {transfer.status === 'w_transporcie' && isFromSourceWarehouse(transfer) && (
                          <span style={{ fontSize: '9px', color: 'var(--info)' }}>🚚 W drodze</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create transfer modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '0.5rem'
        }}>
          <div style={{
            background: "var(--card-bg)", borderRadius: '6px', width: '700px', maxWidth: '95vw', maxHeight: '90vh',
            overflow: 'auto', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            {/* Header */}
            <div style={{
              padding: '0.6rem 1rem',
              border: '1px solid var(--success)', color: 'var(--success)' , display: 'flex', alignItems: 'center', gap: '0.75rem',
              borderRadius: '6px 6px 0 0'
            }}>
              <div style={{
                width: '1.75rem', height: '1.75rem', background: 'var(--bg-secondary)',
                borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem'
              }}>
                🔄
              </div>
              <div style={{ flex: 1 }}>
                <h6 style={{ margin: 0, fontWeight: '600', fontSize: '12px' }}>
                  Nowy transfer mi{'ę'}dzymagazynowy
                </h6>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{ background: 'none',  fontSize: '1.1rem', color: 'var(--text-secondary)', cursor: 'pointer', padding: '0.25rem', lineHeight: 1 }}
              >
                {'✕'}
              </button>
            </div>

            <form onSubmit={handleCreateTransfer}>
              <div style={{ padding: '0.75rem 1rem', fontSize: '11px' }}>
                {/* Warehouse selectors */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                      Magazyn {'ź'}r{'ó'}d{'ł'}owy * {selectedLocation ? `(${selectedLocation.name || selectedLocation.nazwa})` : ''}
                    </label>
                    <select
                      style={{ width: '100%', fontSize: '11px', padding: '0.4rem', border: '1px solid var(--border)', borderRadius: '4px' }}
                      value={transferForm.magazyn_zrodlowy_id}
                      onChange={(e) => setTransferForm({...transferForm, magazyn_zrodlowy_id: e.target.value})}
                      required
                    >
                      <option value="">Wybierz magazyn {'ź'}r{'ó'}d{'ł'}owy</option>
                      {sourceWarehouses.map(warehouse => (
                        <option key={warehouse.id} value={warehouse.id}>
                          {warehouse.nazwa} ({warehouse.kod_magazynu || warehouse.kod})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                      Magazyn docelowy *
                    </label>
                    <select
                      style={{ width: '100%', fontSize: '11px', padding: '0.4rem', border: '1px solid var(--border)', borderRadius: '4px' }}
                      value={transferForm.magazyn_docelowy_id}
                      onChange={(e) => setTransferForm({...transferForm, magazyn_docelowy_id: e.target.value})}
                      required
                    >
                      <option value="">Wybierz magazyn docelowy</option>
                      {targetWarehouses.map(warehouse => (
                        <option
                          key={warehouse.id}
                          value={warehouse.id}
                          disabled={warehouse.id === parseInt(transferForm.magazyn_zrodlowy_id)}
                        >
                          {warehouse.lokalizacja_nazwa ? `${warehouse.lokalizacja_nazwa} — ` : ''}
                          {warehouse.nazwa} ({warehouse.kod_magazynu || warehouse.kod})
                          {warehouse.id === parseInt(transferForm.magazyn_zrodlowy_id) ? ' (źródłowy)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Data dokumentu */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                    Data dokumentu
                  </label>
                  <input
                    type="date"
                    style={{ fontSize: '11px', padding: '0.4rem', border: '1px solid var(--border)', borderRadius: '4px', backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}
                    value={transferForm.document_date}
                    onChange={(e) => setTransferForm({...transferForm, document_date: e.target.value})}
                  />
                </div>

                {/* Notes */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>
                    Uwagi
                  </label>
                  <textarea
                    style={{ width: '100%', fontSize: '11px', padding: '0.4rem', border: '1px solid var(--border)', borderRadius: '4px', minHeight: '50px' }}
                    value={transferForm.uwagi}
                    onChange={(e) => setTransferForm({...transferForm, uwagi: e.target.value})}
                    placeholder="Opcjonalne uwagi do transferu..."
                  />
                </div>

                {/* Products section */}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h6 style={{ fontSize: '12px', fontWeight: '600', margin: 0, color: 'var(--text-secondary)' }}>
                      📦 Produkty do przeniesienia
                    </h6>
                    <label style={{
                      display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '10px', cursor: 'pointer',
                      color: onlyAvailable ? 'var(--success)' : 'var(--text-muted)'
                    }}>
                      <input
                        type="checkbox"
                        checked={onlyAvailable}
                        onChange={(e) => setOnlyAvailable(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                      Tylko dost{'ę'}pne
                    </label>
                  </div>

                  {/* Product search autocomplete */}
                  <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
                    <input
                      type="text"
                      style={{
                        width: '100%', fontSize: '11px', padding: '0.5rem',
                        border: selectedProduct ? '2px solid var(--success)' : '1px solid var(--border)',
                        borderRadius: '4px',
                        backgroundColor: selectedProduct ? 'var(--success-light)' : 'var(--card-bg)'
                      }}
                      placeholder="Wpisz min. 2 znaki aby wyszukać produkt..."
                      value={productSearch}
                      onChange={(e) => {
                        setProductSearch(e.target.value);
                        setSelectedProduct(null);
                      }}
                      onFocus={() => {
                        if (productSearch.length >= 2 && filteredProducts.length > 0) {
                          setShowProductDropdown(true);
                        }
                      }}
                    />

                    {/* Dropdown */}
                    {showProductDropdown && filteredProducts.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0,
                        maxHeight: '200px', overflowY: 'auto',
                        backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', borderTop: 'none',
                        borderRadius: '0 0 4px 4px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 1000
                      }}>
                        {filteredProducts.map(product => (
                          <div
                            key={product.id}
                            onClick={() => handleSelectProduct(product)}
                            style={{
                              padding: '0.5rem', cursor: 'pointer', borderBottom: '1px solid var(--bg)',
                              fontSize: '11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--bg)'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--card-bg)'}
                          >
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '500' }}>{product.name || product.nazwa}</div>
                              <div style={{ color: 'var(--text-muted)', fontSize: '10px' }}>
                                {product.barcode || product.kod_kreskowy || 'brak kodu'}
                              </div>
                            </div>
                            <div style={{ textAlign: 'center', padding: '0 0.5rem', minWidth: '50px' }}>
                              <div style={{
                                fontWeight: '700', fontSize: '11px',
                                color: (product.stock_quantity || 0) > 0 ? 'var(--success)' : 'var(--danger)'
                              }}>
                                {product.stock_quantity || 0}
                              </div>
                              <div style={{ color: 'var(--text-muted)', fontSize: '8px' }}>dost{'ę'}pne</div>
                            </div>
                            <div style={{ textAlign: 'right', minWidth: '60px' }}>
                              <div style={{ color: 'var(--info)', fontWeight: '600' }}>
                                {(product.current_purchase_price || product.cena_zakupu || 0).toFixed(2)} z{'ł'}
                              </div>
                              <div style={{ color: 'var(--text-muted)', fontSize: '8px' }}>cena zak.</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Products list */}
                  {transferForm.items.length > 0 ? (
                    <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid var(--border)', borderRadius: '4px' }}>
                      <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
                        <thead style={{ backgroundColor: 'var(--bg)', position: 'sticky', top: 0 }}>
                          <tr>
                            <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Produkt</th>
                            <th style={{ padding: '0.3rem 0.4rem', textAlign: 'center', borderBottom: '1px solid var(--border)', width: '55px' }}>Dost{'ę'}pne</th>
                            <th style={{ padding: '0.3rem 0.4rem', textAlign: 'center', borderBottom: '1px solid var(--border)', width: '70px' }}>Ilo{'ść'}</th>
                            <th style={{ padding: '0.3rem 0.4rem', textAlign: 'center', borderBottom: '1px solid var(--border)', width: '80px' }}>Cena zak.</th>
                            <th style={{ padding: '0.3rem 0.4rem', textAlign: 'right', borderBottom: '1px solid var(--border)', width: '65px' }}>Warto{'ść'}</th>
                            <th style={{ padding: '0.3rem 0.4rem', textAlign: 'center', borderBottom: '1px solid var(--border)', width: '30px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {transferForm.items.map((item, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid var(--border)' }}>
                              <td style={{ padding: '0.3rem 0.4rem' }}>
                                <div style={{ fontWeight: '500', fontSize: '10px' }}>{item.nazwa_produktu}</div>
                                <code style={{ fontSize: '8px', backgroundColor: 'var(--border)', padding: '1px 3px', borderRadius: '2px', color: 'var(--text-muted)' }}>
                                  {item.kod_kreskowy}
                                </code>
                              </td>
                              <td style={{
                                padding: '0.3rem 0.4rem', textAlign: 'center', fontWeight: '600', fontSize: '10px',
                                color: item.dostepna_ilosc > 0 ? (item.ilosc_wyslana > item.dostepna_ilosc ? 'var(--danger)' : 'var(--success)') : 'var(--danger)'
                              }}>
                                {item.dostepna_ilosc || 0}
                                {item.ilosc_wyslana > item.dostepna_ilosc && (
                                  <span title="Ilość przekracza dostępny stan" style={{ marginLeft: '2px' }}>{'⚠️'}</span>
                                )}
                              </td>
                              <td style={{ padding: '0.2rem' }}>
                                <input
                                  type="number"
                                  style={{
                                    width: '100%', fontSize: '10px', padding: '0.25rem',
                                    border: `1px solid ${item.ilosc_wyslana > item.dostepna_ilosc ? 'var(--danger)' : 'var(--border)'}`,
                                    borderRadius: '3px', textAlign: 'center',
                                    backgroundColor: item.ilosc_wyslana > item.dostepna_ilosc ? 'var(--danger-light)' : 'var(--card-bg)'
                                  }}
                                  step="1" min="1"
                                  value={item.ilosc_wyslana}
                                  onChange={(e) => handleUpdateItemQuantity(index, e.target.value)}
                                />
                              </td>
                              <td style={{ padding: '0.2rem' }}>
                                <input
                                  type="number"
                                  style={{
                                    width: '100%', fontSize: '10px', padding: '0.25rem',
                                    border: '1px solid var(--border)', borderRadius: '3px', textAlign: 'center'
                                  }}
                                  step="0.01" min="0"
                                  value={item.cena_jednostkowa}
                                  onChange={(e) => handleUpdateItemPrice(index, e.target.value)}
                                />
                              </td>
                              <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right', fontWeight: '600', color: 'var(--success)' }}>
                                {(item.ilosc_wyslana * item.cena_jednostkowa).toFixed(2)} z{'ł'}
                              </td>
                              <td style={{ padding: '0.2rem', textAlign: 'center' }}>
                                <button
                                  type="button"
                                  style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--danger)', color: 'var(--danger)' , cursor: 'pointer' }}
                                  onClick={() => handleRemoveProduct(index)}
                                >
                                  {'✕'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot style={{ backgroundColor: 'var(--border)' }}>
                          <tr>
                            <td colSpan="4" style={{ padding: '0.4rem', textAlign: 'right', fontWeight: '600' }}>
                              Razem ({transferForm.items.length} poz.):
                            </td>
                            <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: '700', color: 'var(--success)' }}>
                              {transferForm.items.reduce((sum, item) => sum + (item.ilosc_wyslana * item.cena_jednostkowa), 0).toFixed(2)} z{'ł'}
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', backgroundColor: 'var(--bg)', borderRadius: '4px', fontSize: '11px' }}>
                      📦 Wyszukaj i kliknij produkt aby doda{'ć'} do listy
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button
                  type="button"
                  style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' , cursor: 'pointer' }}
                  onClick={() => setShowCreateModal(false)}
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '6px 12px', fontSize: '12px', fontWeight: '600',
                    backgroundColor: transferForm.items.length === 0 ? 'var(--success-light)' : 'var(--success)',
                    color: '#fff' ,
                    cursor: transferForm.items.length === 0 ? 'not-allowed' : 'pointer'
                  }}
                  disabled={loading || transferForm.items.length === 0}
                >
                  {loading ? 'Tworzenie...' : 'Utwórz transfer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transfer details modal */}
      <DetailsModal
        show={showDetailsModal && !!selectedTransfer}
        onClose={() => setShowDetailsModal(false)}
        title={`Szczegóły transferu ${selectedTransfer?.numer_transferu || ''}`}
        headerBg="linear-gradient(135deg, var(--cyan), var(--cyan))"
        maxWidth="650px"
        footerButtons={
          <>
            {/* Status info */}
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', flex: 1 }}>
              {selectedTransfer && (selectedTransfer.status === 'oczekujacy' || selectedTransfer.status === 'zatwierdzony') && isFromDestinationWarehouse(selectedTransfer) && (
                <span>{'⏳'} Oczekuje na wysy{'ł'}k{'ę'} z magazynu {'ź'}r{'ó'}d{'ł'}owego</span>
              )}
              {selectedTransfer && selectedTransfer.status === 'w_transporcie' && isFromSourceWarehouse(selectedTransfer) && (
                <span>🚚 Transfer w drodze - oczekuje na odbi{'ó'}r</span>
              )}
              {selectedTransfer && selectedTransfer.status === 'dostarczony' && (
                <span>{'✅'} Transfer zako{'ń'}czony</span>
              )}
            </div>
            <button
              style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' , cursor: 'pointer' }}
              onClick={() => setShowDetailsModal(false)}
            >
              Zamknij
            </button>
            {selectedTransfer && (selectedTransfer.status === 'oczekujacy' || selectedTransfer.status === 'zatwierdzony') && isFromSourceWarehouse(selectedTransfer) && (
              <button
                style={{ padding: '6px 12px', fontSize: '12px', backgroundColor: 'var(--warning)', color: 'var(--text)', cursor: 'pointer' }}
                onClick={() => handleTransferAction(selectedTransfer.id, 'ship')}
              >
                📦 Wy{'ś'}lij
              </button>
            )}
            {selectedTransfer && selectedTransfer.status === 'w_transporcie' && isFromDestinationWarehouse(selectedTransfer) && (
              <button
                style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--success)', color: 'var(--success)' , cursor: 'pointer' }}
                onClick={() => handleTransferAction(selectedTransfer.id, 'receive')}
              >
                {'✅'} Odbierz
              </button>
            )}
          </>
        }
      >
        {selectedTransfer && (
          <div style={{ fontSize: '11px' }}>
            {/* Info grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem', padding: '0.5rem', backgroundColor: 'var(--bg)', borderRadius: '4px' }}>
              <div>
                <p style={{ margin: '0.2rem 0' }}><strong>Status:</strong> {getStatusBadge(selectedTransfer.status)}</p>
                <p style={{ margin: '0.2rem 0' }}><strong>Magazyn {'ź'}r{'ó'}d{'ł'}owy:</strong> {selectedTransfer.magazyn_zrodlowy_nazwa || getWarehouseName(selectedTransfer.magazyn_zrodlowy_id)}</p>
              </div>
              <div>
                <p style={{ margin: '0.2rem 0' }}><strong>Data utworzenia:</strong> {new Date(selectedTransfer.data_zlozenia || selectedTransfer.created_at).toLocaleString()}</p>
                <p style={{ margin: '0.2rem 0' }}><strong>Magazyn docelowy:</strong> {selectedTransfer.magazyn_docelowy_nazwa || getWarehouseName(selectedTransfer.magazyn_docelowy_id)}</p>
              </div>
            </div>

            {selectedTransfer.uwagi && (
              <div style={{ marginBottom: '0.75rem', padding: '0.4rem', backgroundColor: 'var(--warning-light)', borderRadius: '4px' }}>
                <strong>Uwagi:</strong> {selectedTransfer.uwagi}
              </div>
            )}

            {/* Products */}
            {selectedTransfer.items && selectedTransfer.items.length > 0 && (
              <>
                <h6 style={{ fontSize: '11px', fontWeight: '600', marginBottom: '0.5rem' }}>📦 Produkty:</h6>
                <div className="table-responsive" style={{ maxHeight: '200px', overflow: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: 'var(--text)', color: '#fff' , position: 'sticky', top: 0 }}>
                      <tr>
                        <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Produkt</th>
                        <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Kod</th>
                        <th style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>Ilo{'ść'}</th>
                        <th style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>Dostarczone</th>
                        <th style={{ padding: '0.3rem 0.4rem', textAlign: 'left' }}>Jednostka</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTransfer.items.map(item => (
                        <tr key={item.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '0.3rem 0.4rem' }}>{item.nazwa_produktu}</td>
                          <td style={{ padding: '0.3rem 0.4rem' }}>
                            <code style={{ fontSize: '9px', backgroundColor: 'var(--border)', padding: '1px 3px', borderRadius: '2px' }}>{item.kod_kreskowy}</code>
                          </td>
                          <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>{item.ilosc_zlecona}</td>
                          <td style={{ padding: '0.3rem 0.4rem', textAlign: 'right' }}>{item.ilosc_dostarczona > 0 ? item.ilosc_dostarczona : '-'}</td>
                          <td style={{ padding: '0.3rem 0.4rem' }}>{item.jednostka || 'szt'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </DetailsModal>
    </div>
  );
};

export default InterWarehouseTransfer;
